<template>
  <section :class="embedded ? 'w-full' : 'w-full rounded border border-slate-600 bg-slate-800/30'" aria-labelledby="local-gpio-heading">
    <div v-if="!embedded" class="flex flex-wrap items-center justify-between gap-2 border-b border-slate-600 bg-slate-700/50 px-3 py-2">
      <div>
        <h2 id="local-gpio-heading" class="text-sm font-semibold text-white">Local GPIO</h2>
        <div class="mt-0.5 text-[11px] text-slate-400">
          Plate: <span class="text-slate-200">{{ plateName || 'not selected' }}</span>
          <span class="mx-1 text-slate-600">·</span>
          Metadata: <span :class="statusClass">{{ statusLabel }}</span>
        </div>
      </div>
      <div v-if="status.error" class="max-w-full text-[11px] text-amber-300" :title="status.error">
        Descriptor warning
      </div>
    </div>
    <div v-else class="border-b border-slate-700/80 px-3 py-1.5 text-[11px] text-slate-400">
      Plate: <span class="text-slate-200">{{ plateName || 'not selected' }}</span>
      <span class="mx-1 text-slate-600">·</span>
      Metadata: <span :class="statusClass">{{ statusLabel }}</span>
      <span v-if="status.error" class="ml-2 text-amber-300" :title="status.error">Descriptor warning</span>
    </div>

    <div v-for="section in sections" :key="section.mode" class="border-b border-slate-700/80 last:border-b-0">
      <div class="flex items-center justify-between px-3 py-2">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-300">{{ section.title }}</h3>
        <button
          type="button"
          class="rounded px-2 py-0.5 text-lg leading-none text-cyan-300 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="bindingsFor(section.mode).length >= MAX_BINDINGS || !objectSupports(section.mode)"
          :title="objectSupports(section.mode) ? `Add ${section.title}` : `No ${section.title.toLowerCase()} supported for this object type`"
          @click="addBinding(section.mode)">+</button>
      </div>

      <div v-if="!bindingsFor(section.mode).length" class="px-3 pb-3 text-xs text-slate-500">
        No {{ section.title.toLowerCase() }} configured.
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full min-w-[48rem] table-fixed">
          <thead class="bg-slate-800/40 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th class="w-[30%] px-3 py-1.5 text-left font-medium">{{ section.mode === 'event' ? 'Event / source' : 'Entity / property' }}</th>
              <th class="w-[32%] px-3 py-1.5 text-left font-medium">{{ section.mode === 'event' ? 'GPIO target' : 'Target property' }}</th>
              <th class="w-[24%] px-3 py-1.5 text-left font-medium">Mapping / status</th>
              <th class="w-24 px-3 py-1.5 text-right font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-700/70">
            <template v-for="(binding, index) in bindingsFor(section.mode)" :key="`${section.mode}-${index}`">
              <tr class="text-xs" :class="isExpanded(section.mode, index) ? 'bg-cyan-950/20' : 'hover:bg-slate-700/20'">
                <td class="px-3 py-2">
                  <button type="button" class="flex min-w-0 items-center gap-1.5 text-left" @click="toggleExpanded(section.mode, index)">
                    <span class="text-slate-500">{{ isExpanded(section.mode, index) ? '▾' : '▸' }}</span>
                    <span class="truncate text-slate-200" :title="bindingSummary(binding, section.mode, entityFor(binding))">
                      {{ summaryPrimary(binding, section.mode) }}
                    </span>
                  </button>
                  <div class="ml-4 truncate font-mono text-[11px] text-slate-500">{{ summarySecondary(binding, section.mode) }}</div>
                </td>
                <td class="px-3 py-2 text-slate-300">
                  <span class="block truncate" :class="binding.entity ? 'font-mono text-slate-200' : 'text-amber-300'">{{ targetSummary(binding, section.mode) }}</span>
                </td>
                <td class="px-3 py-2">
                  <span v-if="mappingVisible(binding, section.mode, node.obj)" class="text-slate-400">{{ mappingSummary(binding, section.mode) }}</span>
                  <span v-else class="text-slate-600">—</span>
                  <span v-if="rowWarnings(binding, section.mode).length" class="ml-1 text-amber-300" :title="rowWarnings(binding, section.mode).map(issue => issue.message).join('\n')">⚠</span>
                  <span v-if="rowErrors(binding, section.mode).length" class="ml-1 text-red-300" :title="rowErrors(binding, section.mode).map(issue => issue.message).join('\n')">✕</span>
                </td>
                <td class="px-3 py-2">
                  <div class="flex justify-end gap-1">
                    <button type="button" class="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-cyan-300" title="Edit GPIO binding" @click="toggleExpanded(section.mode, index)">✎</button>
                    <button type="button" class="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-cyan-300" title="Duplicate GPIO binding" @click="duplicateBinding(section.mode, index)">⧉</button>
                    <button type="button" class="rounded p-1 text-red-400 hover:bg-red-500/10 hover:text-red-300" title="Remove GPIO binding" @click="removeBinding(section.mode, index)">✕</button>
                  </div>
                </td>
              </tr>
              <tr v-if="isExpanded(section.mode, index)" class="bg-slate-900/20">
                <td colspan="4" class="border-t border-slate-700/70 px-3 py-3">
                  <div class="grid grid-cols-1 gap-3 @md:grid-cols-2 @3xl:grid-cols-4">
                    <div v-if="section.mode === 'event'" class="min-w-0">
                      <label class="mb-1 block text-[11px] text-slate-400">Event</label>
                      <BuilderSelect class="w-full text-xs" :modelValue="binding.event" @update:modelValue="updateBinding(binding, 'event', $event)">
                        <option v-for="event in eventOptions" :key="event" :value="event">{{ event }}</option>
                        <option v-if="binding.event && !eventOptions.includes(binding.event)" :value="binding.event">{{ binding.event }} · saved</option>
                      </BuilderSelect>
                    </div>
                    <div v-if="section.mode === 'event'" class="min-w-0">
                      <label class="mb-1 block text-[11px] text-slate-400">Source property</label>
                      <BuilderSelect class="w-full text-xs" :modelValue="binding.source_property" @update:modelValue="updateBinding(binding, 'source_property', $event)">
                        <option value="val">val</option>
                        <option value="state">state</option>
                      </BuilderSelect>
                    </div>
                    <div class="min-w-0 @md:col-span-2">
                      <label class="mb-1 block text-[11px] text-slate-400">GPIO entity</label>
                      <GpioEntitySelector
                        :modelValue="binding.entity"
                        :entities="entities"
                        @update:modelValue="updateBinding(binding, 'entity', $event)" />
                    </div>
                    <div class="min-w-0">
                      <label class="mb-1 block text-[11px] text-slate-400">GPIO property</label>
                      <GpioPropertySelector
                        :modelValue="binding.property"
                        :entity="entityFor(binding)"
                        :mode="section.mode"
                        :objectType="node.obj"
                        :event="binding.event"
                        @update:modelValue="updateBinding(binding, 'property', $event)" />
                    </div>
                    <div v-if="section.mode === 'state'" class="min-w-0">
                      <label class="mb-1 block text-[11px] text-slate-400">Target property</label>
                      <BuilderSelect class="w-full text-xs" :modelValue="binding.target_property" @update:modelValue="updateBinding(binding, 'target_property', $event)">
                        <option v-for="target in targetOptions(binding)" :key="target" :value="target">{{ target }}</option>
                        <option v-if="binding.target_property && !targetOptions(binding).includes(binding.target_property)" :value="binding.target_property">{{ binding.target_property }} · saved</option>
                      </BuilderSelect>
                    </div>

                    <div v-if="mappingVisible(binding, section.mode, node.obj)" class="@md:col-span-2 @3xl:col-span-4">
                      <div class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Mapping</div>
                      <div class="grid grid-cols-2 gap-2 @md:grid-cols-4">
                        <div>
                          <label class="mb-1 block text-[10px] text-slate-500">{{ section.mode === 'event' ? 'UI min' : 'GPIO min' }}</label>
                          <BuilderInput type="number" class="w-full text-xs" :modelValue="mappingValue(binding, 'in_min', section.mode)" @update:modelValue="setRange(binding, 'in_min', $event)" />
                        </div>
                        <div>
                          <label class="mb-1 block text-[10px] text-slate-500">{{ section.mode === 'event' ? 'UI max' : 'GPIO max' }}</label>
                          <BuilderInput type="number" class="w-full text-xs" :modelValue="mappingValue(binding, 'in_max', section.mode)" @update:modelValue="setRange(binding, 'in_max', $event)" />
                        </div>
                        <div>
                          <label class="mb-1 block text-[10px] text-slate-500">{{ section.mode === 'event' ? 'GPIO min' : 'UI min' }}</label>
                          <BuilderInput type="number" class="w-full text-xs" :modelValue="mappingValue(binding, 'out_min', section.mode)" @update:modelValue="setRange(binding, 'out_min', $event)" />
                        </div>
                        <div>
                          <label class="mb-1 block text-[10px] text-slate-500">{{ section.mode === 'event' ? 'GPIO max' : 'UI max' }}</label>
                          <BuilderInput type="number" class="w-full text-xs" :modelValue="mappingValue(binding, 'out_max', section.mode)" @update:modelValue="setRange(binding, 'out_max', $event)" />
                        </div>
                      </div>
                      <p v-if="!hasExplicitMapping(binding)" class="mt-1 text-[11px] text-cyan-300">Using suggested firmware ranges; edit a value to store an explicit mapping.</p>
                    </div>

                    <div v-if="section.mode === 'event' && bidirectionalAvailable(binding)" class="@md:col-span-2 @3xl:col-span-4">
                      <label class="flex items-center gap-2 text-xs text-slate-300">
                        <input type="checkbox" :checked="hasBidirectionalState(binding)" :disabled="!hasBidirectionalState(binding) && stateBindings.length >= MAX_BINDINGS" class="rounded border-slate-500 bg-slate-800 text-cyan-500" @change="toggleBidirectional(binding, $event.target.checked)" />
                        Bidirectional (also create a State Binding)
                      </label>
                      <p class="mt-1 text-[11px] text-slate-500">The linked State Binding remains visible and can be edited separately.</p>
                    </div>

                    <div class="@md:col-span-2 @3xl:col-span-4">
                      <div v-if="rowIssues(binding, section.mode).length" class="space-y-1 text-[11px]">
                        <div v-for="(issue, issueIndex) in rowIssues(binding, section.mode)" :key="issueIndex" :class="issue.severity === 'error' ? 'text-red-300' : 'text-amber-300'">
                          {{ issue.severity === 'error' ? '❌' : '⚠' }} {{ issue.message }}
                        </div>
                      </div>
                      <div v-else class="text-[11px] text-emerald-300">✓ Binding is valid for the current firmware matrix.</div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import BuilderInput from '../hassbuilder/automation/BuilderInput.vue'
import BuilderSelect from '../hassbuilder/automation/BuilderSelect.vue'
import GpioEntitySelector from './GpioEntitySelector.vue'
import GpioPropertySelector from './GpioPropertySelector.vue'
import gpioMetadataProvider from '../../gpio/GpioMetadataProvider.js'
import {
  GPIO_MAX_BINDINGS,
  canonicalizeGpioBindings,
  validateGpioBindings,
} from '../../gpio/GpioBindingModel.js'
import {
  bindingDiagnostics,
  bindingSummary,
  cloneBinding,
  createEventBinding,
  createStateBinding,
  eventOptionsForObject,
  mappingFallback,
  mappingVisible,
  statePropertyOptions,
  stateTargetOptions,
} from '../../gpio/gpioEditorModel.js'

const props = defineProps({
  node: { type: Object, required: true },
  settings: { type: Object, required: true },
  embedded: { type: Boolean, default: false },
  stateOnly: { type: Boolean, default: false },
})
const emit = defineEmits(['validation'])

const sections = computed(() => props.stateOnly
  ? [{ mode: 'state', title: 'State Bindings' }]
  : [
      { mode: 'state', title: 'State Bindings' },
      { mode: 'event', title: 'Event Actions' },
    ])
const expanded = ref('')
const metadataVersion = ref(0)
const snapshot = ref(null)
let stopSubscription = null

const plate = computed(() => props.settings?.plate ?? null)
const plateName = computed(() => plate.value?.name ?? '')
const entities = computed(() => snapshot.value?.entities ?? gpioMetadataProvider.listEntities(plate.value))
const status = computed(() => snapshot.value?.status ?? gpioMetadataProvider.getStatus(plate.value))
const statusLabel = computed(() => {
  const current = status.value
  if (!current.available) return 'unknown'
  if (current.online) return `${current.entityCount} entities · connected`
  if (current.source === 'plate') return `${current.entityCount} entities · plate configuration`
  return `${current.entityCount} entities · cached/offline`
})
const statusClass = computed(() => status.value.online ? 'text-emerald-300' : (status.value.available ? 'text-amber-300' : 'text-slate-500'))
const eventOptions = computed(() => eventOptionsForObject(props.node.obj))
const stateBindings = computed(() => bindingsFor('state'))

function bindingsFor(mode) {
  return props.node.gpio_bindings?.[mode === 'event' ? 'events' : 'state'] ?? []
}

function objectSupports(mode) {
  if (mode === 'event') return eventOptions.value.length > 0
  return statePropertyOptions(props.node.obj).some(property => stateTargetOptions(props.node.obj, props.node, property).length > 0)
}

function ensureStore() {
  if (!props.node.gpio_bindings) props.node.gpio_bindings = { events: [], state: [] }
  props.node.gpio_bindings = canonicalizeGpioBindings(props.node.gpio_bindings) ?? { events: [], state: [] }
  return props.node.gpio_bindings
}

function cloneValue(value) {
  return value === undefined ? null : JSON.parse(JSON.stringify(value))
}

function prepareHistory() {
  const layer = props.node.getLayer?.()
  if (!layer || layer._historySuppress) return
  layer._beforeColors ??= {}
  if (!Object.hasOwn(layer._beforeColors, 'gpio_bindings')) {
    layer._beforeColors.gpio_bindings = cloneValue(props.node.gpio_bindings)
  }
}

function commitHistory() {
  props.node.getLayer?.()?.layerCallback?.('gpio-bindings-changed', props.node)
}

function entityFor(binding) {
  return entities.value.find(entity => entity.id === binding.entity) ?? null
}

function addBinding(mode) {
  prepareHistory()
  const store = ensureStore()
  const list = mode === 'event' ? store.events : store.state
  if (list.length >= GPIO_MAX_BINDINGS) return
  const entity = entities.value[0] ?? null
  list.push(mode === 'event' ? createEventBinding(props.node, entity) : createStateBinding(props.node, entity))
  expanded.value = `${mode}:${list.length - 1}`
  notifyValidation()
  commitHistory()
}

function duplicateBinding(mode, index) {
  prepareHistory()
  const store = ensureStore()
  const list = mode === 'event' ? store.events : store.state
  if (list.length >= GPIO_MAX_BINDINGS) return
  list.splice(index + 1, 0, cloneBinding(list[index]))
  expanded.value = `${mode}:${index + 1}`
  notifyValidation()
  commitHistory()
}

function removeBinding(mode, index) {
  prepareHistory()
  const store = props.node.gpio_bindings
  if (!store) return
  const list = mode === 'event' ? store.events : store.state
  list.splice(index, 1)
  if (!store.events.length && !store.state.length) delete props.node.gpio_bindings
  expanded.value = ''
  notifyValidation()
  commitHistory()
}

function updateBinding(binding, key, value) {
  prepareHistory()
  binding[key] = value
  notifyValidation()
  commitHistory()
}

function setRange(binding, key, value) {
  prepareHistory()
  const numeric = Number(value)
  binding[key] = Number.isFinite(numeric) ? Math.trunc(numeric) : value
  notifyValidation()
  commitHistory()
}

function targetOptions(binding) {
  return stateTargetOptions(props.node.obj, props.node, binding.property)
}

function mappingValue(binding, key, mode) {
  return mappingFallback(binding, key, mode, props.node, entityFor(binding))
}

function hasExplicitMapping(binding) {
  return ['in_min', 'in_max', 'out_min', 'out_max'].some(key => binding[key] !== undefined)
}

function mappingSummary(binding, mode) {
  return `${mappingValue(binding, 'in_min', mode)}→${mappingValue(binding, 'in_max', mode)} · ${mappingValue(binding, 'out_min', mode)}→${mappingValue(binding, 'out_max', mode)}`
}

function summaryPrimary(binding, mode) {
  return mode === 'event' ? `${binding.event || 'event'} · ${binding.source_property || 'val'}` : `${binding.entity || 'entity'}.${binding.property || 'property'}`
}

function summarySecondary(binding, mode) {
  return mode === 'event' ? `${binding.entity || 'entity'}.${binding.property || 'property'}` : `→ ${binding.target_property || 'target'}`
}

function targetSummary(binding, mode) {
  return mode === 'event' ? (binding.entity ? `${binding.entity}.${binding.property || 'property'}` : 'Select entity') : (binding.target_property || 'Select target')
}

function isExpanded(mode, index) { return expanded.value === `${mode}:${index}` }
function toggleExpanded(mode, index) { expanded.value = isExpanded(mode, index) ? '' : `${mode}:${index}` }

function rowIssues(binding, mode) {
  return bindingDiagnostics(binding, mode, props.node, snapshot.value?.descriptor ?? null)
}
function rowErrors(binding, mode) { return rowIssues(binding, mode).filter(issue => issue.severity === 'error') }
function rowWarnings(binding, mode) { return rowIssues(binding, mode).filter(issue => issue.severity === 'warning') }

function bidirectionalAvailable(binding) {
  const propertySupported = statePropertyOptions(props.node.obj).includes(binding.property)
  const targetSupported = stateTargetOptions(props.node.obj, props.node, binding.property).includes('val')
  return propertySupported && targetSupported && eventOptions.value.includes(binding.event)
}

function hasBidirectionalState(binding) {
  return stateBindings.value.some(state => state.entity === binding.entity && state.property === binding.property && state.target_property === 'val')
}

function toggleBidirectional(binding, checked) {
  prepareHistory()
  const store = ensureStore()
  const index = store.state.findIndex(state => state.entity === binding.entity && state.property === binding.property && state.target_property === 'val')
  if (checked && index < 0 && store.state.length < GPIO_MAX_BINDINGS) {
    store.state.push(createStateBinding(props.node, entityFor(binding)))
    const created = store.state[store.state.length - 1]
    created.entity = binding.entity
    created.property = binding.property
    created.target_property = 'val'
    created.in_min = mappingValue(binding, 'out_min', 'event')
    created.in_max = mappingValue(binding, 'out_max', 'event')
    created.out_min = props.node.min ?? 0
    created.out_max = props.node.max ?? 100
  } else if (!checked && index >= 0) {
    store.state.splice(index, 1)
  }
  notifyValidation()
  commitHistory()
}

function notifyValidation() {
  const result = validateGpioBindings(props.node.gpio_bindings, {
    objectType: props.node.obj,
    objectConfig: props.node,
    metadata: snapshot.value?.descriptor ?? null,
  })
  emit('validation', result.valid)
  emit('diagnostics', result)
}

function syncMetadata() {
  gpioMetadataProvider.setSettings(props.settings)
  stopSubscription?.()
  stopSubscription = gpioMetadataProvider.subscribe(plate.value, event => {
    snapshot.value = event
    metadataVersion.value++
    notifyValidation()
  })
  snapshot.value = {
    descriptor: gpioMetadataProvider.listEntities(plate.value).length ? { entities: gpioMetadataProvider.listEntities(plate.value) } : null,
    entities: gpioMetadataProvider.listEntities(plate.value),
    status: gpioMetadataProvider.getStatus(plate.value),
  }
  notifyValidation()
}

// The plate's stored GPIO configuration is an entity source too (see GpioMetadataAdapter).
watch(() => [plate.value?.id, plate.value?.name, JSON.stringify(plate.value?.gpio_config ?? null)], syncMetadata, { immediate: true })
watch(() => props.node.gpio_bindings, notifyValidation, { deep: true })
onBeforeUnmount(() => stopSubscription?.())
</script>
